import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Capacitor } from '@capacitor/core';
import { AppModule } from './app/app.module';
import { loadSupportSurface } from '../../lib-shared/data/services/support-surface.service';
import { applySpacingTheme } from '../../lib-design/theme/tokens/spacings';
import { getStoredSpacingScale } from '../../lib-design/theme-selection';
import { environment } from './environments/environment';

/**
 * TRACKING STRATEGY: Platform-Specific Analytics
 * 
 * WEB PLATFORM:
 * - GTM (Google Tag Manager) for conversion tracking
 * - HubSpot Live Chat for customer support
 * - Tracking pixels (LinkedIn, Google Ads) for attribution
 * - Application Insights for telemetry
 * 
 * NATIVE PLATFORMS (iOS/Android):
 * - Firebase Analytics (only) - provides sufficient analytics
 * - Disables GTM, HubSpot, and tracking pixels
 * - These are blocked by WKWebView sandbox or non-functional on native anyway
 * 
 * Benefit: Eliminates 5+ second tracking pixel delays on native platforms
 * that were causing iOS navigation to hang while waiting for Zone stability
 */

// HubSpot Live Chat - Only initialize on web platforms
// On native (iOS/Android), HubSpot doesn't work in Capacitor WebView anyway
if (!Capacitor.isNativePlatform()) {
    window.hsConversationsSettings = {
        loadImmediately: false,
        identificationEmail: undefined,
        identificationToken: undefined,
        inlineEmbedSelector: '#hubspot-cleverly-support-chat',
    };
    console.log('[TRACKING] HubSpot: Enabled for web platform');
} else {
    console.log('[TRACKING] HubSpot: Disabled for native platform');
}

if (environment.production) {
    enableProdMode();
}

// emit the theme's responsive --spacing-*/--main-border scale before first paint
applySpacingTheme(getStoredSpacingScale());

platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .then(() => {
        // Inject HubSpot AFTER Angular bootstraps, on web only.
        // Injecting here ensures:
        //   1. Never loads on native (iOS/Android) – prevents HubSpot's analytics SDK,
        //      GTM integration, and LinkedIn/Google Ads pixels from firing CapacitorHttp
        //      requests inside Angular Zone, which was causing 87-90 second navigation hangs.
        //   2. Runs outside Angular Zone context – HubSpot's recurring setTimeout timers
        //      are created in root zone, not Angular zone, so they don't affect Zone stability.
        //   3. Only when the server-side support-surface switch says so (#1098): the
        //      Backoffice FeaturesSettings.SupportChatClientSurface (served via
        //      api/v1/environment) decides widget vs. in-app chat without a redeploy.
        //      Fetch failure falls back to 'inApp' — the in-app chat avatar remains.
        if (!Capacitor.isNativePlatform()) {
            void loadSupportSurface(environment.apiUrl).then((surface) => {
                if (surface !== 'hubspotWidget') {
                    console.log(`[TRACKING] HubSpot: Skipped — support surface is '${surface}'`);
                    return;
                }
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.id = 'hs-script-loader';
                script.async = true;
                script.defer = true;
                script.src = environment.production
                    ? 'https://js-eu1.hs-scripts.com/139708563.js'
                    : 'https://js-eu1.hs-scripts.com/144892368.js';
                document.body.appendChild(script);
                console.log('[TRACKING] HubSpot: Script injected for web platform');
            });
        } else {
            console.log('[TRACKING] HubSpot: Skipped on native platform');
        }
    })
    .catch((err) => console.error(err));
